import React from 'react';
import PropTypes from 'prop-types';
import { get, orderBy } from 'lodash';
import { CREATIVES_REPORT_VIEW } from 'constants/capabilities';
import ProtectedComponent from 'hoc/ProtectedComponent';
import RenderingTile from 'components/molecules/RenderingTile';
import CreativesPivotTable from 'components/molecules/tables/CreativesPivotTable';
import TwoAxisBarWithScatterChart from 'components/molecules/charts/TwoAxisBarWithScatterChart';
import BarChart from 'components/molecules/charts/BarChart';

const ProtectedCreativesReports = ProtectedComponent({ requiredCapabilities: [CREATIVES_REPORT_VIEW] })((props) => {
    const {
        accessors,
        hasCreativesData,
        showPlatform = false,
        smartTable = false,
        cacheKey = null,
        creativesParams,
        showTotalActivities,
        showConversionRevenue,
        eCPAActivitiesOrClicks,
        eCPAActivitiesOrClicksTerm,
        activitiesOrClicksRenderFlag,
        entityObjectiveLabelCode,
        exportName,
        creativeNameOverride = 'Creative',
        labelCodeOverride = null,
        channel = null
    } = props;
    const byLabel = (get(props, ['creativesData', 'byLabel'], [])).sort((a, b) => a.creativeLabel.localeCompare(b.creativeLabel));
    const sortedDataByImpressions =
        hasCreativesData ? orderBy(byLabel, ['impressions'], ['desc']) : [];
    const bySize = get(props, ['creativesData', 'bySize'], []);
    const useTwoAxisBar = hasCreativesData && activitiesOrClicksRenderFlag;
    const ChartType = useTwoAxisBar ? TwoAxisBarWithScatterChart : BarChart;
    const chartProps = useTwoAxisBar ?
        {
            exportName: `${exportName}-bar-and-scatter-impressions-vs-kpi`,
            title: `TOP IMPRESSIONS VS. ${eCPAActivitiesOrClicksTerm} BY ${creativeNameOverride.toUpperCase()}`,
            yBar: 'impressions',
            scatter: eCPAActivitiesOrClicks,
            scatterTitle: eCPAActivitiesOrClicksTerm,
        } :
        {
            exportName: `${exportName}-bar-impressions`,
            title: `TOP IMPRESSIONS BY ${creativeNameOverride.toUpperCase()}`,
            y: 'impressions',
            yAxisLabel: 'Impressions',
        };

    return (
        <React.Fragment>
            {hasCreativesData ? (
                <ChartType
                    {...chartProps}
                    creativeNameOverride={creativeNameOverride}
                    data={sortedDataByImpressions}
                    showTopOnly
                    categories={sortedDataByImpressions.map(c => c.creativeLabel)}
                    hasCreativesData={hasCreativesData}
                    animateDuration={2000}
                />
            ) : (
                <RenderingTile height='400px' show={hasCreativesData} />
            )}
            {hasCreativesData ? (
                <CreativesPivotTable
                    showPlatform={showPlatform}
                    creativeNameOverride={creativeNameOverride}
                    smartTable={smartTable}
                    cacheKey={cacheKey}
                    labelCodeOverride={labelCodeOverride}
                    exportName={`${exportName}-pivot-table`}
                    hasCreativesData={hasCreativesData}
                    creativesData={bySize}
                    creativesParams={creativesParams}
                    showTotalActivities={showTotalActivities}
                    showConversionRevenue={showConversionRevenue}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                    accessors={accessors}
                    channel={channel}
                />
            ) : (
                <RenderingTile height='400px' show={hasCreativesData} />
            )}
        </React.Fragment>
    );
});

ProtectedCreativesReports.propTypes = {
    hasCreativesData: PropTypes.bool,
    showTotalActivities: PropTypes.bool,
    eCPAActivitiesOrClicks: PropTypes.string,
    eCPAActivitiesOrClicksTerm: PropTypes.string,
    entityObjectiveLabelCode: PropTypes.string,
    creativesData: PropTypes.shape({
        byLabel: PropTypes.arrayOf(PropTypes.object),
        bySize: PropTypes.arrayOf(PropTypes.object)
    }),
    creativesParams: PropTypes.object,
    accessors: PropTypes.arrayOf(PropTypes.string),
};

export default ProtectedCreativesReports;



// WEBPACK FOOTER //
// ./src/components/organisms/ProtectedCreativesReports.js